/** Client-side mesh loaders: STL, OBJ, 3MF (mesh only). Output Y-up Float32 positions. */

const EXT_OK = new Set(['stl', 'obj', '3mf']);

function extOf(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name.trim());
  return (m?.[1] || '').toLowerCase();
}

function stlBinaryToYUp(buf: ArrayBuffer): Float32Array {
  if (buf.byteLength < 84) throw new Error('STL file is too small to be valid');
  const dv = new DataView(buf);
  const tris = dv.getUint32(80, true);
  const expected = 84 + tris * 50;
  if (tris <= 0 || tris > 20_000_000) throw new Error('STL triangle count looks invalid');
  if (buf.byteLength < expected - 50) {
    // Some writers pad; require at least most of the payload
    throw new Error('STL binary size does not match triangle count');
  }
  const out = new Float32Array(tris * 9);
  let o = 84;
  let p = 0;
  for (let i = 0; i < tris; i++) {
    o += 12; // skip normal
    for (let v = 0; v < 3; v++) {
      const x = dv.getFloat32(o, true);
      const y = dv.getFloat32(o + 4, true);
      const z = dv.getFloat32(o + 8, true);
      // Z-up STL → Y-up
      out[p++] = x;
      out[p++] = z;
      out[p++] = -y;
      o += 12;
    }
    o += 2;
  }
  return out;
}

function isAsciiStl(buf: ArrayBuffer): boolean {
  if (buf.byteLength < 15) return false;
  const head = new TextDecoder().decode(buf.slice(0, Math.min(256, buf.byteLength)));
  // Binary STLs sometimes start with "solid" too — check for null bytes / facet
  const hasNull = new Uint8Array(buf.slice(0, 80)).includes(0);
  if (hasNull) return false;
  const lower = head.toLowerCase();
  if (!lower.includes('solid')) return false;
  // Prefer ASCII if we see facet/vertex early, or if declared size doesn't match binary
  if (/facet\s+normal|vertex\s+/i.test(head)) return true;
  if (buf.byteLength >= 84) {
    const tris = new DataView(buf).getUint32(80, true);
    const expected = 84 + tris * 50;
    if (Math.abs(expected - buf.byteLength) > 512) return true;
  }
  return lower.trimStart().startsWith('solid');
}

function stlAsciiToYUp(text: string): Float32Array {
  const verts: number[] = [];
  const re = /vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const x = parseFloat(m[1]);
    const y = parseFloat(m[2]);
    const z = parseFloat(m[3]);
    verts.push(x, z, -y);
  }
  if (verts.length < 9) throw new Error('ASCII STL contained no triangles');
  return new Float32Array(verts);
}

async function loadStl(file: File): Promise<Float32Array> {
  const buf = await file.arrayBuffer();
  if (isAsciiStl(buf)) {
    return stlAsciiToYUp(new TextDecoder().decode(buf));
  }
  return stlBinaryToYUp(buf);
}

async function loadObj(file: File): Promise<Float32Array> {
  const text = await file.text();
  const positions: number[] = [];
  const verts: number[][] = [];
  for (const line of text.split(/\r?\n/)) {
    const p = line.trim().split(/\s+/);
    if (p[0] === 'v' && p.length >= 4) verts.push([+p[1], +p[2], +p[3]]);
    else if (p[0] === 'f') {
      const idx = p.slice(1).map((t) => parseInt(t.split('/')[0], 10) - 1);
      for (let i = 1; i + 1 < idx.length; i++) {
        for (const vi of [idx[0], idx[i], idx[i + 1]]) {
          const v = verts[vi];
          if (!v) throw new Error('OBJ face references a missing vertex');
          positions.push(v[0], v[1], v[2]);
        }
      }
    }
  }
  return new Float32Array(positions);
}

/** Minimal 3MF mesh extract (first object mesh vertices/triangles). */
async function load3mf(file: File): Promise<Float32Array> {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const modelFile =
    zip.file(/3D\/.*\.model$/i)[0] ||
    zip.file(/.*\.model$/i)[0];
  if (!modelFile) throw new Error('No 3D model found inside 3MF');
  const xml = await modelFile.async('string');
  const verts: number[][] = [];
  const positions: number[] = [];
  const vRe = /<vertex\s+x="([^"]+)"\s+y="([^"]+)"\s+z="([^"]+)"/gi;
  let m: RegExpExecArray | null;
  while ((m = vRe.exec(xml))) verts.push([+m[1], +m[2], +m[3]]);
  const tRe = /<triangle\s+v1="(\d+)"\s+v2="(\d+)"\s+v3="(\d+)"/gi;
  while ((m = tRe.exec(xml))) {
    for (const vi of [+m[1], +m[2], +m[3]]) {
      const v = verts[vi];
      positions.push(v[0], v[2], -v[1]);
    }
  }
  if (!positions.length) throw new Error('Empty mesh in 3MF');
  return new Float32Array(positions);
}

/** Sniff format when extension is missing (iOS sometimes strips it). */
async function sniffFormat(file: File): Promise<'stl' | 'obj' | '3mf' | null> {
  const buf = await file.slice(0, 512).arrayBuffer();
  const bytes = new Uint8Array(buf);
  // ZIP / 3MF
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return '3mf';
  const text = new TextDecoder().decode(buf);
  if (/^\s*solid\b/i.test(text) || /facet\s+normal/i.test(text)) return 'stl';
  if (/^\s*#/.test(text) || /\bv\s+[-+\d.]/.test(text)) return 'obj';
  // Binary STL has no reliable magic — only guess when size looks like STL
  if (file.size >= 84) return 'stl';
  return null;
}

export interface LoadedMesh {
  name: string;
  positions: Float32Array;
}

export async function loadMeshFile(file: File): Promise<LoadedMesh> {
  let ext = extOf(file.name);
  if (!EXT_OK.has(ext)) {
    const sniffed = await sniffFormat(file);
    if (!sniffed) {
      throw new Error(
        `Unsupported file “${file.name || 'unnamed'}”. Please choose an .stl, .3mf, or .obj model.`
      );
    }
    ext = sniffed;
  }
  const base = (file.name || `model.${ext}`).replace(/\.(stl|obj|3mf)$/i, '');
  let positions: Float32Array;
  if (ext === 'stl') positions = await loadStl(file);
  else if (ext === 'obj') positions = await loadObj(file);
  else if (ext === '3mf') positions = await load3mf(file);
  else throw new Error(`Unsupported format: ${ext}`);
  if (positions.length < 9) throw new Error('Mesh has no triangles');
  return { name: base || 'model', positions };
}

/** Accept string safe for desktop; on iOS omit accept entirely. */
export const FILE_ACCEPT_DESKTOP =
  '.stl,.3mf,.obj,model/stl,model/3mf,model/obj,application/sla,application/vnd.ms-pki.stl,application/octet-stream';

export function isAppleTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return iOS;
}
